import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon } from '@mui/icons-material';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { partySchema, type PartyFormValues } from '@/schemas';
import { FormTextField } from '@/components';
import { createParty } from '@/api/parties';
import { brand } from '@/theme/theme';

/**
 * Create Party form.
 *
 * Mounted at `/network/new`. The "Create party" / "+ NEW PARTY" buttons
 * on the parties list page navigate here.
 *
 * Validation is driven by the `partySchema` Zod schema in
 * `src/schemas/index.ts`, which mirrors backend `PartyDto`. Same rules
 * apply to typed input here and to programmatic API callers.
 *
 * Layout: two columns matching the design reference — identity / address
 * fields on the left, contact / regulatory fields on the right.
 */
export function PartyFormPage() {
  const navigate = useNavigate();
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<PartyFormValues>({
    resolver: zodResolver(partySchema),
    defaultValues: {
      name: '',
      partyType: 'RETAILER',
      defaultPricing: 'PTR',
      partyCode: '',
      partyExternalId: '',
      contactPerson: '',
      mobile: '',
      phone: '',
      email: '',
      gstin: '',
      panNumber: '',
      dlNumber: '',
      dlExpiry: '',
      repName: '',
      addressLine1: '',
      addressLine2: '',
      area: '',
      city: '',
      pincode: '',
    },
  });

  const onSubmit = async (values: PartyFormValues) => {
    setServerError(null);
    setSubmitting(true);
    try {
      // Strip empty optional fields so the backend doesn't store ""
      // in columns the user left blank.
      const cleaned: PartyFormValues = {
        ...values,
        partyExternalId: values.partyExternalId?.trim() || undefined,
        contactPerson: values.contactPerson?.trim() || undefined,
        mobile: values.mobile?.trim() || undefined,
        phone: values.phone?.trim() || undefined,
        email: values.email?.trim() || undefined,
        gstin: values.gstin?.trim() || undefined,
        panNumber: values.panNumber?.trim() || undefined,
        dlNumber: values.dlNumber?.trim() || undefined,
        dlExpiry: values.dlExpiry?.trim() || undefined,
        repName: values.repName?.trim() || undefined,
        addressLine1: values.addressLine1?.trim() || undefined,
        addressLine2: values.addressLine2?.trim() || undefined,
        area: values.area?.trim() || undefined,
        city: values.city?.trim() || undefined,
        pincode: values.pincode?.trim() || undefined,
      };
      await createParty(cleaned);
      navigate('/network', { replace: true, state: { created: true } });
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = err as any;
      const msg =
        e?.response?.data?.message ||
        (e?.response?.status === 409 && 'A party with this code already exists.') ||
        (e?.response?.status === 400 && 'Some fields are invalid. Please review.') ||
        'Failed to save party. Please try again.';
      setServerError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/network')}
          sx={{ color: brand.inkSoft }}
        >
          Back to parties
        </Button>
      </Stack>

      <Typography variant="h2" sx={{ color: brand.navy, mb: 0.5 }}>
        Create Party
      </Typography>
      <Typography variant="body2" sx={{ color: brand.inkSoft, mb: 3 }}>
        Add a new B2B customer to your party book. Required fields are marked with *.
      </Typography>

      {serverError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setServerError(null)}>
          {serverError}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <Grid container spacing={2}>
              {/* ---------- LEFT COLUMN — identity + address ---------- */}
              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <FormTextField
                    name="name"
                    register={register}
                    errors={errors}
                    label="Party Name *"
                    placeholder="Party Name"
                    autoFocus
                  />

                  {/* Party type — drives the chip on the parties list */}
                  <Controller
                    name="partyType"
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        select
                        size="small"
                        fullWidth
                        label="Party Type"
                        value={field.value ?? ''}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      >
                        <MenuItem value="RETAILER">Retailer</MenuItem>
                        <MenuItem value="WHOLESALER">Wholesaler</MenuItem>
                        <MenuItem value="DISTRIBUTOR">Distributor</MenuItem>
                        <MenuItem value="OTHERS">Others</MenuItem>
                      </TextField>
                    )}
                  />

                  {/* Default pricing tier — drives the radio on the list */}
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ color: brand.inkSoft, display: 'block', mb: 0.5 }}
                    >
                      Default Pricing
                    </Typography>
                    <Controller
                      name="defaultPricing"
                      control={control}
                      render={({ field }) => (
                        <ToggleButtonGroup
                          {...field}
                          exclusive
                          size="small"
                          value={field.value ?? 'PTR'}
                          onChange={(_, v) => v && field.onChange(v)}
                          sx={{ width: '100%' }}
                        >
                          <ToggleButton value="PTR" sx={{ flex: 1 }}>
                            PTR
                          </ToggleButton>
                          <ToggleButton value="PTS" sx={{ flex: 1 }}>
                            PTS
                          </ToggleButton>
                        </ToggleButtonGroup>
                      )}
                    />
                  </Box>

                  <FormTextField
                    name="partyCode"
                    register={register}
                    errors={errors}
                    label="Party Code *"
                    placeholder="Code"
                  />
                  <FormTextField
                    name="partyExternalId"
                    register={register}
                    errors={errors}
                    label="Party ID"
                    placeholder="Party ID"
                  />
                  <FormTextField
                    name="addressLine1"
                    register={register}
                    errors={errors}
                    label="Address Line 1"
                    placeholder="Address Line 1"
                  />
                  <FormTextField
                    name="addressLine2"
                    register={register}
                    errors={errors}
                    label="Address Line 2"
                    placeholder="Address Line 1"
                  />
                  <FormTextField
                    name="area"
                    register={register}
                    errors={errors}
                    label="Area"
                    placeholder="Area"
                  />
                  <FormTextField
                    name="city"
                    register={register}
                    errors={errors}
                    label="City"
                    placeholder="City"
                  />
                  <FormTextField
                    name="pincode"
                    register={register}
                    errors={errors}
                    label="Pincode"
                    placeholder="Pincode"
                    inputProps={{ inputMode: 'numeric' }}
                  />
                </Stack>
              </Grid>

              {/* ---------- RIGHT COLUMN — contact + regulatory ---------- */}
              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <FormTextField
                    name="contactPerson"
                    register={register}
                    errors={errors}
                    label="Contact Person"
                    placeholder="Contact Person"
                  />
                  <FormTextField
                    name="mobile"
                    register={register}
                    errors={errors}
                    label="Mobile"
                    placeholder="Mobile"
                    inputProps={{ inputMode: 'tel' }}
                  />
                  <FormTextField
                    name="phone"
                    register={register}
                    errors={errors}
                    label="Phone"
                    placeholder="Phone"
                    inputProps={{ inputMode: 'tel' }}
                  />
                  <FormTextField
                    name="email"
                    register={register}
                    errors={errors}
                    label="Email"
                    placeholder="Email"
                    type="email"
                  />
                  <FormTextField
                    name="gstin"
                    register={register}
                    errors={errors}
                    label="GSTIN"
                    placeholder="GSTIN"
                  />
                  <FormTextField
                    name="panNumber"
                    register={register}
                    errors={errors}
                    label="PAN Number"
                    placeholder="PAN Number"
                  />
                  <FormTextField
                    name="dlNumber"
                    register={register}
                    errors={errors}
                    label="DL Number"
                    placeholder="DL Number"
                  />
                  <FormTextField
                    name="dlExpiry"
                    register={register}
                    errors={errors}
                    label="DL Expiry"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                  />
                  <FormTextField
                    name="repName"
                    register={register}
                    errors={errors}
                    label="Rep Name"
                    placeholder="Rep Name"
                  />
                </Stack>
              </Grid>
            </Grid>

            <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ mt: 3 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/network')}
                disabled={submitting}
                sx={{ minWidth: 140 }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={submitting}
                startIcon={
                  submitting ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />
                }
                sx={{ minWidth: 140 }}
              >
                {submitting ? 'Saving…' : 'Submit'}
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}
